import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deleteItem, updateItem } from "../api";
import { useItemsWithAuth } from "../hooks/useItemsWithAuth";
import { Category, ShoppingListItem } from "../types";
import { buildFaviconUrl, normalizeLinks } from "../utils/linkHelpers";
import { CollapseIcon, EditIcon, ExpandIcon, PendingIcon, PlusIcon, QuantityIcon, TrashIcon, CartIcon } from "./Icons";

const categoryLabels: Record<Category, string> = {
  toy: "Toy",
  clothing: "Clothing",
  consumable: "Consumable",
  home: "Home"
};

const categories: Array<Category | "all"> = ["all", "toy", "clothing", "consumable", "home"];

export default function ItemListPage() {
  const navigate = useNavigate();
  const { items, setItems, loading, error, setError } = useItemsWithAuth();
  const [filter, setFilter] = useState<Category | "all">("all");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"website" | "updated" | "title">("updated");
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (sortOpen && sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [sortOpen]);

  const handleDelete = async (id: string) => {
    if (deletingId) return;
    const confirmDelete = window.confirm("Delete this item?");
    if (!confirmDelete) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteItem(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError((err as Error).message ?? "Unable to delete item");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    const list = filter === "all" ? items : items.filter((item) => item.category === filter);
    return [...list].sort((a, b) => {
      const pendingFirst = Number(a.got) - Number(b.got);
      if (pendingFirst !== 0) return pendingFirst;
      if (sortBy === "updated") {
        return b.updatedAt.localeCompare(a.updatedAt);
      }
      if (sortBy === "title") {
        return a.name.localeCompare(b.name);
      }
      const primaryA = (normalizeLinks(a)[0] ?? "").toLowerCase();
      const primaryB = (normalizeLinks(b)[0] ?? "").toLowerCase();
      return primaryA.localeCompare(primaryB);
    });
  }, [items, filter, sortBy]);

  const toggleGot = async (item: ShoppingListItem) => {
    if (togglingId) return;
    setTogglingId(item.id);
    setError(null);
    const nextGot = !item.got;
    try {
      await updateItem(item.id, {
        category: item.category,
        name: item.name,
        description: item.description ?? "",
        urls: Array.isArray(item.urls) ? item.urls : item.url ? [item.url] : [],
        url: (Array.isArray(item.urls) ? item.urls[0] : undefined) ?? item.url ?? "",
        reference: Array.isArray(item.reference) ? item.reference : [],
        got: nextGot,
        quantity: item.quantity
      });
      setItems((prev) =>
        prev.map((existing) =>
          existing.id === item.id ? { ...existing, got: nextGot, updatedAt: new Date().toISOString() } : existing
        )
      );
    } catch (err) {
      setError((err as Error).message ?? "Unable to update item");
    } finally {
      setTogglingId(null);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading) {
    return <div>Loading items…</div>;
  }

  if (error === "signin-required") {
    return (
      <div className="error" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <span>Redirecting to login</span>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      <div className="page-actions" style={{ alignItems: "flex-start", gap: "1rem", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div ref={sortRef} style={{ position: "relative", display: "inline-flex", flexDirection: "column", gap: "0.35rem" }}>
          <button
            type="button"
            onClick={() => setSortOpen((prev) => !prev)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setSortOpen(false);
            }}
            className="link-button"
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              padding: "0.4rem 0.75rem",
              borderRadius: "10px",
              background: "var(--card-surface, #fff)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              minWidth: "150px",
              textAlign: "left",
              color: "inherit",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.5rem"
            }}
          >
            <span>{sortBy === "website" ? "Website" : sortBy === "title" ? "Title" : "Updated"}</span>
            <span aria-hidden>▾</span>
          </button>
          {sortOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                background: "var(--card-surface, #fff)",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "10px",
                boxShadow: "0 14px 32px rgba(0,0,0,0.16)",
                minWidth: "170px",
                zIndex: 10,
                overflow: "hidden"
              }}
            >
              {["website", "updated", "title"].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setSortBy(option as typeof sortBy);
                    setSortOpen(false);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "0.6rem 0.9rem",
                    border: "none",
                    background: sortBy === option ? "rgba(14,165,233,0.12)" : "transparent",
                    cursor: "pointer"
                  }}
                >
                  {option === "website" ? "Website" : option === "title" ? "Title" : "Updated"}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", justifyContent: "flex-end", flex: "1 1 auto" }}>
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className="link-button"
              style={{ opacity: filter === category ? 1 : 0.5 }}
              onClick={() => setFilter(category)}
            >
              {category === "all" ? "All" : categoryLabels[category]}
            </button>
          ))}
          <Link to="/item" className="link-button icon-button" aria-label="Add item">
            <PlusIcon ariaHidden />
            <span className="sr-only">Add item</span>
          </Link>
        </div>
      </div>
      {filteredItems.length === 0 ? (
        <p>No items yet. Use the form to add something fun.</p>
      ) : (
        <div className="item-grid">
          {filteredItems.map((item) => {
            const links = normalizeLinks(item);
            return (
              <article key={item.id} className="item-card">
                <div className="item-head">
                  <div className="item-title" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                    {item.name}
                  </div>
                  <div className="item-meta-row">
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                      {links.map((link, index) => (
                        <a
                          key={`${item.id}-url-${index}`}
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="pill"
                          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", paddingInline: "0.35rem" }}
                          aria-label={links.length > 1 ? `Open URL ${index + 1}` : "Open URL"}
                        >
                          <img
                            src={buildFaviconUrl(link)}
                            alt=""
                            aria-hidden
                            width={16}
                            height={16}
                            style={{ borderRadius: "4px", objectFit: "contain" }}
                            referrerPolicy="no-referrer"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                          <span className="sr-only">{links.length > 1 ? `Open URL ${index + 1}` : "Open URL"}</span>
                        </a>
                      ))}
                    </div>
                    <span className="chip">{categoryLabels[item.category]}</span>
                    <span className="pill pill-quantity">{item.quantity}</span>
                    <button
                      type="button"
                      className={`pill ${item.got ? "pill-success" : "pill-pending"}`}
                      onClick={() => toggleGot(item)}
                      disabled={togglingId === item.id}
                      aria-pressed={item.got}
                    >
                      {togglingId === item.id ? "Saving…" : item.got ? "In cart" : "Pending"}
                    </button>
                    <div className="item-actions">
                      <button
                        type="button"
                        className="link-button icon-button"
                        aria-label={expanded.has(item.id) ? "Hide details" : "Show details"}
                        onClick={() => toggleExpanded(item.id)}
                      >
                        {expanded.has(item.id) ? <CollapseIcon ariaHidden /> : <ExpandIcon ariaHidden />}
                      </button>
                      <button
                        type="button"
                        className="link-button icon-button"
                        onClick={() => navigate(`/item/${item.id}`)}
                        aria-label="Edit item"
                      >
                        <EditIcon ariaHidden />
                      </button>
                      <button
                        type="button"
                        className="link-button icon-button"
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                        aria-label={deletingId === item.id ? "Deleting" : "Delete item"}
                      >
                        {deletingId === item.id ? "…" : <TrashIcon ariaHidden />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="item-body">
                  <div className={`item-extra ${expanded.has(item.id) ? "expanded" : ""}`}>
                    <p className="item-description">{item.description ?? "No description"}</p>
                    <div className="item-references">
                      {(() => {
                        const refs = Array.isArray(item.reference) ? item.reference.filter(Boolean) : [];
                        if (!refs.length) {
                          return <span className="reference-pill" aria-disabled="true">No references</span>;
                        }
                        return refs.map((ref) => (
                          <a
                            key={ref}
                            className="reference-pill"
                            href={ref}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {ref}
                          </a>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
      <img className="floating-bunny" src="/bunny.gif" alt="Bunny" />
    </div>
  );
}
